// Inbox, preference matrix and project silencing. Query keys live in ./queries so the
// `notification` change event can invalidate them through invalidationsFor().

import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import type {
  MuteDTO,
  NotificationCategory,
  NotificationPageDTO,
  NotificationPreferencesDTO,
  NotificationSeverity,
} from '@devrobot/shared';
import { api, seg } from './client';
import { qk, useApiMutation, type NotificationFilters } from './queries';

const PAGE = 30;

export function useNotifications(f: NotificationFilters = {}) {
  return useInfiniteQuery({
    queryKey: qk.notifications(f),
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      api.get<NotificationPageDTO>('/api/v1/notifications', {
        query: { unread: f.unread ? '1' : undefined, category: f.category, project: f.project, limit: PAGE, before: pageParam },
      }),
    getNextPageParam: (last) => last.nextCursor,
  });
}

/**
 * The bell's count. Polling is a backstop only: the SSE `notification` frame invalidates
 * this the moment something arrives, so the interval just covers a dropped stream.
 */
export const useUnreadCount = () =>
  useQuery({
    queryKey: qk.notificationsUnread,
    queryFn: () => api.get<{ unread: number }>('/api/v1/notifications/unread-count'),
    refetchInterval: 120_000,
    staleTime: 30_000,
  });

export const useNotificationPrefs = () =>
  useQuery({ queryKey: qk.notificationPrefs, queryFn: () => api.get<NotificationPreferencesDTO>('/api/v1/notifications/preferences') });

export const useMarkRead = () =>
  useApiMutation((ids: string[]) => api.post<{ updated: number }>('/api/v1/notifications/read', { ids }), {
    invalidate: [['notifications']],
  });

export const useMarkAllRead = () =>
  useApiMutation((vars: { before?: string; category?: string } = {}) => api.post<{ updated: number }>('/api/v1/notifications/read-all', vars), {
    invalidate: [['notifications']],
  });

export const useDismissNotification = () =>
  useApiMutation((id: string) => api.delete<void>(`/api/v1/notifications/${seg(id)}`), { invalidate: [['notifications']] });

export type PreferencePatch = Partial<Record<NotificationCategory, { inapp?: boolean; push?: boolean; rocketchat?: boolean; minSeverity?: NotificationSeverity }>>;

export const useUpdatePrefs = () =>
  useApiMutation((patch: PreferencePatch) => api.patch<NotificationPreferencesDTO>('/api/v1/notifications/preferences', patch), {
    invalidate: [['notifications']],
  });

export const useSilenceProject = () =>
  useApiMutation((vars: { projectKey: string; minutes: number | null }) => api.post<MuteDTO>('/api/v1/notifications/mutes', vars), {
    invalidate: [['notifications']],
  });

export const useResumeProject = () =>
  useApiMutation((projectKey: string) => api.delete<void>(`/api/v1/notifications/mutes/${seg(projectKey)}`), {
    invalidate: [['notifications']],
  });

/* ------------------------------------------------------------------ devices */

export interface PushDeviceDTO {
  id: string;
  label: string;
  createdAt: string;
  lastSeenAt: string;
  lastSuccessAt?: string;
  unreachable: boolean;
}

export const usePushDevices = () =>
  useQuery({ queryKey: [...qk.notificationPrefs, 'devices'], queryFn: () => api.get<PushDeviceDTO[]>('/api/v1/notifications/devices') });

export const useForgetDevice = () =>
  useApiMutation((id: string) => api.delete<void>(`/api/v1/notifications/devices/${seg(id)}`), { invalidate: [['notifications']] });

export interface TestPushResult {
  sent: number;
  failed: { id: string; status: number; error?: string }[];
}

/**
 * Sends a real push to this account's devices. Not a nicety: a bad VAPID signature or an
 * unreachable endpoint is otherwise completely invisible from the browser.
 */
export const useTestPush = () => useApiMutation(() => api.post<TestPushResult>('/api/v1/notifications/test', {}), { invalidate: [['notifications']] });

/* ------------------------------------------------------------- rocket.chat */

export interface RocketChatLinkDTO {
  configured: boolean;
  username?: string;
  verified: boolean;
  pending: boolean;
}

export const useRocketChat = () =>
  useQuery({ queryKey: [...qk.notificationPrefs, 'rocketchat'], queryFn: () => api.get<RocketChatLinkDTO>('/api/v1/notifications/rocketchat') });

export const useLinkRocketChat = () =>
  useApiMutation((username: string) => api.post<RocketChatLinkDTO>('/api/v1/notifications/rocketchat/link', { username }), { invalidate: [['notifications']] });

export const useVerifyRocketChat = () =>
  useApiMutation((code: string) => api.post<RocketChatLinkDTO>('/api/v1/notifications/rocketchat/verify', { code }), { invalidate: [['notifications']] });

export const useUnlinkRocketChat = () =>
  useApiMutation(() => api.delete<void>('/api/v1/notifications/rocketchat/link'), { invalidate: [['notifications']] });

/* ------------------------------------------------------------ integrations */

export interface IntegrationDTO {
  id: string;
  kind: 'webhook' | 'rocketchat';
  urlHost: string;
  categories: NotificationCategory[];
  minSeverity: NotificationSeverity;
  enabled: boolean;
  hasSecret: boolean;
  createdAt: string;
  lastDeliveryAt?: string;
  lastStatus?: number;
  consecutiveFailures: number;
  disabledReason?: string;
}

export const useProjectIntegrations = (projectKey: string) =>
  useQuery({
    queryKey: ['project', projectKey, 'integrations'],
    queryFn: () => api.get<IntegrationDTO[]>(`/api/v1/projects/${seg(projectKey)}/integrations`),
  });

const integrationKeys = (projectKey: string) => [['project', projectKey, 'integrations']];

export const useCreateIntegration = (projectKey: string) =>
  useApiMutation(
    (vars: { kind: 'webhook' | 'rocketchat'; url: string; categories?: string[]; minSeverity?: NotificationSeverity }) =>
      api.post<{ integration: IntegrationDTO; secret?: string }>(`/api/v1/projects/${seg(projectKey)}/integrations`, vars),
    { invalidate: integrationKeys(projectKey) },
  );

export const useUpdateIntegration = (projectKey: string) =>
  useApiMutation(
    (vars: { id: string; categories?: string[]; minSeverity?: NotificationSeverity; enabled?: boolean }) =>
      api.patch<IntegrationDTO>(`/api/v1/projects/${seg(projectKey)}/integrations/${seg(vars.id)}`, vars),
    { invalidate: integrationKeys(projectKey) },
  );

export const useDeleteIntegration = (projectKey: string) =>
  useApiMutation((id: string) => api.delete<void>(`/api/v1/projects/${seg(projectKey)}/integrations/${seg(id)}`), { invalidate: integrationKeys(projectKey) });

export const useTestIntegration = (projectKey: string) =>
  useApiMutation((id: string) => api.post<{ status: number; ok: boolean; error?: string }>(`/api/v1/projects/${seg(projectKey)}/integrations/${seg(id)}/test`, {}), {
    invalidate: integrationKeys(projectKey),
  });

export const useRotateIntegrationSecret = (projectKey: string) =>
  useApiMutation((id: string) => api.post<{ secret: string }>(`/api/v1/projects/${seg(projectKey)}/integrations/${seg(id)}/rotate-secret`, {}), {
    invalidate: integrationKeys(projectKey),
  });

/* -------------------------------------------------- rocket.chat (platform) */

/**
 * The bot credential is write-only: it is stored encrypted and never returned, so these
 * only ever set or clear it. Whether one exists is reported by `rocketchatConfigured` on
 * the platform settings.
 */
export const useSetRocketChatCredentials = () =>
  useApiMutation((vars: { authToken: string; userId: string }) => api.put<{ configured: boolean }>('/api/v1/settings/rocketchat-credentials', vars), {
    invalidate: [['settings'], ['notifications']],
  });

export const useClearRocketChatCredentials = () =>
  useApiMutation(() => api.delete<void>('/api/v1/settings/rocketchat-credentials'), { invalidate: [['settings'], ['notifications']] });
